import { callFunction } from "./functions-client";

// Fleet-OS F4 — safety incident capture ("dashcam-lite" on the phone). Shared client used
// by both the web console and mob-app via the @rt alias. Media is validated server-side by
// magic bytes; a caller never chooses the storage path or extension.
export const INCIDENT_KINDS = ["collision", "near_miss", "hard_event", "roadside", "theft", "other"] as const;
export type IncidentKind = (typeof INCIDENT_KINDS)[number];

export const INCIDENT_LABELS: Record<IncidentKind, string> = {
  collision: "Collision", near_miss: "Near miss", hard_event: "Hard braking / swerve",
  roadside: "Roadside / breakdown", theft: "Theft / security", other: "Other",
};

export const REVIEW_STATUSES = ["open", "reviewed", "dismissed"] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

export interface Incident {
  id: string;
  shipment_id: string | null;
  kind: IncidentKind;
  severity: number;
  note: string | null;
  lat: number | null;
  lng: number | null;
  occurred_at: string;
  media_type: string | null;
  media_url: string | null;
  review_status: ReviewStatus;
  retain_until: string;
  created_at: string;
}

export const reportIncident = (input: {
  kind: IncidentKind; severity?: number; note?: string; lat?: number; lng?: number; occurred_at?: string; shipment_id?: string;
}) => callFunction<{ ok: boolean; id: string }>("incidents/report", { body: input });

export const incidentMediaUrl = (incident_id: string) =>
  callFunction<{ path: string; upload_url: string }>("incidents/media-url", { body: { incident_id } });

export const finalizeIncidentMedia = (incident_id: string, path: string) =>
  callFunction<{ ok: boolean; media_type: string }>("incidents/media-finalize", { body: { incident_id, path } });

export const listIncidents = (shipment_id?: string) =>
  callFunction<{ incidents: Incident[] }>(
    `incidents/list${shipment_id ? `?shipment_id=${encodeURIComponent(shipment_id)}` : ""}`, { method: "GET" });

export const reviewIncident = (incident_id: string, review_status: ReviewStatus) =>
  callFunction<{ ok: boolean }>("incidents/review", { body: { incident_id, review_status } });

/** Report an incident, then upload + finalize its media in one call. Returns the incident id. */
export async function reportIncidentWithMedia(
  input: Parameters<typeof reportIncident>[0],
  media: Blob,
  upload: (uploadUrl: string, blob: Blob) => Promise<void>,
): Promise<string> {
  const { id } = await reportIncident(input);
  const { path, upload_url } = await incidentMediaUrl(id);
  await upload(upload_url, media);
  await finalizeIncidentMedia(id, path);
  return id;
}
